// Student Types - Frontend type definitions for student panel

// Query DTOs
export interface GetStudentClassesQueryDto {
  academicYearId?: string;
  semester?: number;
}

// GET /student/academic-years — years the student can consult (history + current)
export interface StudentAcademicYear {
  id: string;
  name: string;
  isCurrent: boolean;
}

// Schedule Info
export interface ScheduleInfoItem {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  roomName: string | null;
}

// Student Class Info
export interface StudentClassInfo {
  moduleElementId: string;
  moduleElementName: string;
  moduleCode: string;
  moduleName: string;
  groupeId: string;
  groupeName: string;
  filiereId: string;
  filiereName: string;
  niveau: number;
  semester: number;
  professorId: string;
  professorName: string;
  campusId: string | null;
  campusName: string | null;
  scheduleInfo: ScheduleInfoItem[];
  attendanceRate: number;
  pendingAssignments: number;
  gradeAverage: number | null;
  academicYearId: string;
  academicYearName: string;
  // True when this course instance belongs to a group the student left during
  // the year (or a deleted group): history, consultable but never writable.
  isFormerGroup?: boolean;
}

// Student Class Detail
export interface StudentClassDetail extends StudentClassInfo {
  professorEmail: string | null;
  professorPhone: string | null;
  totalStudents: number;
  announcementsCount: number;
  materialsCount: number;
  assignmentsCount: number;
  syllabusUploaded: boolean;
}

// Grade Types
export interface GradeComponentScore {
  code: string;
  name: string;
  percentage: number;
  maxScore: number;
  score: number | null;
  absenceStatus: 'NONE' | 'ABSENT_EXCUSED' | 'ABSENT_UNEXCUSED';
  gradedAt: string | null;
  // Group whose professor set this component (set when the student changed group)
  groupName?: string | null;
}

export interface StudentGradeBreakdown {
  moduleElementId: string;
  moduleElementName: string;
  components: GradeComponentScore[];
  averageGrade: number | null;
  isComplete: boolean;
  status: 'INCOMPLETE' | 'VALIDATED' | 'FAILED' | 'RATTRAPAGE';
  // Survey gating fields
  surveyRequired: boolean;
  surveyCompleted: boolean;
  cumulativeGradeWeight: number;
  groupeId?: string;
  academicYearId?: string;
}

// Attendance Types
export interface AttendanceRecord {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  status: 'PRESENT' | 'ABSENT_UNEXCUSED' | 'ABSENT_EXCUSED' | 'LATE' | 'NOT_MARKED';
  lateMinutes: number | null;
  justification: string | null;
  // Group the session belonged to (set when the student changed group during the year)
  groupName?: string | null;
}

export interface StudentAttendanceHistory {
  moduleElementId: string;
  moduleElementName: string;
  totalSessions: number;
  attendanceRate: number;
  presentCount: number;
  absentExcusedCount: number;
  absentUnexcusedCount: number;
  lateCount: number;
  records: AttendanceRecord[];
}

// Classmate Type (privacy-focused)
export interface ClassmateInfo {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl: string | null;
}

// Assignment Types
export interface StudentAssignmentResponse {
  id: string;
  title: string;
  description: string | null;
  dueDate: string;
  maxScore: number;
  isPastDue: boolean;
  allowLateSubmission: boolean;
  lateSubmissionDays: number | null;
  hasSubmitted: boolean;
  submissionStatus: 'not_submitted' | 'submitted' | 'late' | 'graded';
  score: number | null;
  createdAt: string;
}

export interface AttachmentFile {
  name: string;
  url: string;
  size?: number;
  type?: string;
}

export interface StudentSubmissionResponse {
  id: string;
  fileUrl: string | null;
  fileName: string | null;
  fileSize: number | null;
  content: string | null;
  submittedAt: string;
  isLate: boolean;
  lateDays: number | null;
  score: number | null;
  feedback: string | null;
  gradedAt: string | null;
  status: string;
}

export interface StudentAssignmentDetail extends StudentAssignmentResponse {
  instructions: string | null;
  attachments: AttachmentFile[];
  rubricUrl: string | null;
  rubricFileName: string | null;
  rubricContent: string | null; // HTML content for rubric
  acceptedFileTypes: string[] | null;
  latePenaltyPercent: number | null;
  mySubmission: StudentSubmissionResponse | null;
  academicYearId: string;
  // False when the assignment belongs to a past year: consultable, never submittable
  isCurrentYear: boolean;
  // True when the assignment belongs to a former group of the student (read-only)
  isFormerGroup?: boolean;
}

// Submission DTOs
export interface SubmitAssignmentDto {
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  content?: string;
}

export interface UpdateSubmissionDto {
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  content?: string;
}

// Announcement Types
export interface StudentAnnouncementResponse {
  id: string;
  title: string;
  content: string;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
}

// Syllabus Types
export interface StudentSyllabusResponse {
  id: string;
  fileUrl: string;
  fileName: string;
  description: string | null;
  objectives: string | null;
  prerequisites: string | null;
  evaluationCriteria: string | null;
  uploadedAt: string;
}

// Content Types
export interface CourseMaterialResponse {
  id: string;
  title: string;
  description: string | null;
  type: string;
  fileUrl: string | null;
  fileName: string | null;
  fileSize: number | null;
  externalUrl: string | null;
  sortOrder: number;
  createdAt: string;
}

export interface CourseChapterResponse {
  id: string;
  title: string;
  description: string | null;
  sortOrder: number;
  materials: CourseMaterialResponse[];
}

export interface StudentCourseContentResponse {
  chapters: CourseChapterResponse[];
  generalMaterials: CourseMaterialResponse[];
  generalSectionTitle: string;
}

// Calendar Types
export interface StudentCalendarEvent {
  id: string;
  title: string;
  description: string | null;
  startDate: string;
  endDate: string;
  categoryId: string;
  categoryCode: string;
  categoryName: string;
  color: string;
}

// Helper function for day names
export const getDayName = (dayOfWeek: number): string => {
  const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  return days[dayOfWeek] || '';
};

// Helper function for short day names
export const getDayNameShort = (dayOfWeek: number): string => {
  const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  return days[dayOfWeek] || '';
};

// Status badge colors
export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'VALIDATED':
      return 'bg-green-100 text-green-800';
    case 'FAILED':
      return 'bg-red-100 text-red-800';
    case 'RATTRAPAGE':
      return 'bg-amber-100 text-amber-800';
    case 'INCOMPLETE':
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Attendance status colors
export const getAttendanceStatusColor = (status: string): string => {
  switch (status) {
    case 'PRESENT':
      return 'bg-green-100 text-green-800';
    case 'LATE':
      return 'bg-amber-100 text-amber-800';
    case 'ABSENT_EXCUSED':
      return 'bg-blue-100 text-blue-800';
    case 'ABSENT_UNEXCUSED':
      return 'bg-red-100 text-red-800';
    case 'NOT_MARKED':
      return 'bg-gray-100 text-gray-500';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Attendance status labels
export const getAttendanceStatusLabel = (status: string): string => {
  switch (status) {
    case 'PRESENT':
      return 'Présent';
    case 'LATE':
      return 'Retard';
    case 'ABSENT_EXCUSED':
      return 'Absence justifiée';
    case 'ABSENT_UNEXCUSED':
      return 'Absence non justifiée';
    case 'NOT_MARKED':
      return 'Non marqué';
    default:
      return status;
  }
};

// ==================== EXAM SCHEDULE TYPES ====================

export interface StudentExamEntry {
  id: string;
  sessionId: string;
  date: string;
  startTime: string;
  endTime: string;
  subject: string;
  moduleName: string;
  durationHours: number;
  room: string;
}

export interface ExamPeriod {
  start: string | null;
  end: string | null;
}

export interface StudentExamScheduleResponse {
  hasExams: boolean;
  message: string | null;
  exams: StudentExamEntry[];
  examPeriod: ExamPeriod | null;
  semester?: number;
  academicYear?: string;
}

// ==================== RATTRAPAGE SCHEDULE TYPES ====================

export interface PendingSubject {
  subject: string;
  moduleName: string;
  originalGrade: number | null;
  reason: string;
}

export interface StudentRattrapageEntry {
  id: string;
  sessionId: string;
  date: string;
  startTime: string;
  endTime: string;
  subject: string;
  moduleName: string;
  durationHours: number;
  room: string;
}

export interface StudentRattrapageScheduleResponse {
  hasRattrapages: boolean;
  message: string | null;
  rattrapages: StudentRattrapageEntry[];
  rattrapagePeriod: ExamPeriod | null;
  /** SINGLE = one yearly rattrapage session (no semester tabs); PER_SEMESTER otherwise */
  periodType?: 'SINGLE' | 'PER_SEMESTER';
  allPassed?: boolean;
  pendingSubjects?: PendingSubject[];
  semester?: number;
  academicYear?: string;
}

// ==================== RATTRAPAGE HISTORY TYPES ====================

export interface RattrapageHistoryEntry {
  id: string;
  subject: string;
  moduleName: string;
  originalGrade: number | null;
  rattrapageGrade: number | null;
  status: string;
  reason: string;
  examDate: string | null;
  examLocation: string | null;
  academicYear: string;
  academicYearId: string;
  semester: number | null;
}

export interface AcademicYearOption {
  id: string;
  name: string;
}

export interface StudentRattrapageHistoryResponse {
  history: RattrapageHistoryEntry[];
  academicYears: AcademicYearOption[];
}

// Helper for rattrapage status
export const getRattrapageStatusLabel = (status: string): string => {
  switch (status) {
    case 'PENDING':
      return 'En attente';
    case 'SCHEDULED':
      return 'Planifié';
    case 'COMPLETED':
      return 'Terminé';
    case 'PASSED':
      return 'Réussi';
    case 'FAILED':
      return 'Échoué';
    case 'CANCELLED':
      return 'Annulé';
    default:
      return status;
  }
};

export const getRattrapageStatusColor = (status: string): string => {
  switch (status) {
    case 'PASSED':
      return 'bg-green-100 text-green-800';
    case 'FAILED':
      return 'bg-red-100 text-red-800';
    case 'SCHEDULED':
      return 'bg-blue-100 text-blue-800';
    case 'PENDING':
      return 'bg-amber-100 text-amber-800';
    case 'COMPLETED':
      return 'bg-gray-100 text-gray-800';
    case 'CANCELLED':
      return 'bg-gray-100 text-gray-500';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const getRattrapageReasonLabel = (reason: string): string => {
  switch (reason) {
    case 'FAILED_GRADE':
      return 'Note insuffisante';
    case 'EXCESSIVE_ABSENCE':
      return 'Absences excessives';
    case 'FAILED_COMPONENT':
      return 'Composante échouée';
    case 'MANUAL':
      return 'Attribution manuelle';
    default:
      return reason;
  }
};
